import { APIConstraintEnforcementMode, ArdoqId } from '@ardoq/api-types';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { getDialogConfigByIds } from 'components/Dialogs/confirmDeletion/helpers';
import { isArdoqError } from '@ardoq/common-helpers';
import { dispatchAction } from '@ardoq/rxbeach';
import { DoqType } from '@ardoq/doq';
import { ConfirmResult, ModalSize, alert, confirm } from '@ardoq/modal';
import { logError } from '@ardoq/logging';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { showRightPane } from 'appContainer/actions';
import { GetContentOptionsType } from 'appModelStateEdit/legacyTypes';
import { v4 as uuidv4 } from 'uuid';
import { IMPLICIT_REFTYPE_ID } from 'models/model';
import {
  createReference,
  deleteReferences,
  deleteReferencesFailed,
  deleteReferencesSuccess,
} from 'streams/references/ReferenceActions';
import { dispatchActionAndWaitForResponse } from 'actions/utils';
import { isScenarioMode } from 'models/utils/scenarioUtils';
import { getFeatureContext } from 'streams/featureContext$';
import { lockOperations } from 'lockedComponents/lockOperations';

const SESSION_KEY_DEPMATRIX_DELETE = '[dependency matrix] Delete reference';

const LockedReferenceDialogText = ({
  referenceNames,
  i18nLocale,
}: {
  referenceNames: string[];
  i18nLocale: I18nLocale;
}) => {
  const isSingular = referenceNames.length === 1;
  return (
    <div>
      <span>
        {t(
          i18nLocale,
          isSingular
            ? 'tabview:dependencyMatrix.referenceCell.locked.bodyOne'
            : 'tabview:dependencyMatrix.referenceCell.locked.bodyOther',
          isSingular
            ? 'The following reference is locked:'
            : 'The following references are locked:'
        )}
      </span>
      <ul>
        {referenceNames.map((name: string, index) => (
          <li key={name + index}>{name}</li>
        ))}
      </ul>
    </div>
  );
};

const shouldOpenRefSidePane = (
  currentReferenceCreationType: number | undefined,
  enforcementMode: APIConstraintEnforcementMode
): boolean => {
  if (currentReferenceCreationType === undefined) return true;
  if (currentReferenceCreationType === IMPLICIT_REFTYPE_ID) {
    return enforcementMode !== 'off';
  }
  return false;
};

type CreateReferenceFromCellArgs = {
  sourceId: ArdoqId;
  targetId: ArdoqId;
  sourceWorkspaceId: ArdoqId | null;
  targetWorkspaceId: ArdoqId | null;
  currentReferenceCreationType: number | undefined;
  constraintEnforcementMode: APIConstraintEnforcementMode;
};

export const createReferenceFromCell = ({
  sourceId,
  targetId,
  sourceWorkspaceId,
  targetWorkspaceId,
  currentReferenceCreationType,
  constraintEnforcementMode,
}: CreateReferenceFromCellArgs): void => {
  if (!targetWorkspaceId || !sourceWorkspaceId) {
    logError(
      Error('Cannot determine workspace IDs for reference creation'),
      null,
      {
        sourceId,
        targetId,
        sourceWorkspaceId,
        targetWorkspaceId,
      }
    );
    return;
  }

  const shouldOpenSidePane = shouldOpenRefSidePane(
    currentReferenceCreationType,
    constraintEnforcementMode
  );

  if (shouldOpenSidePane) {
    const newReferenceAttributes = {
      _id: uuidv4(),
      type: IMPLICIT_REFTYPE_ID,
      rootWorkspace: sourceWorkspaceId,
      model: workspaceInterface.getWorkspaceModelId(sourceWorkspaceId)!,
      source: sourceId,
      target: targetId,
      targetWorkspace: targetWorkspaceId,
    };
    dispatchAction(
      showRightPane({
        newReferenceAttributes: [newReferenceAttributes],
        type: GetContentOptionsType.CREATE_REFERENCE,
        referenceIds: [newReferenceAttributes._id],
      })
    );
  } else {
    dispatchAction(
      createReference({
        attributes: {
          source: sourceId,
          target: targetId,
          type: currentReferenceCreationType,
          rootWorkspace: sourceWorkspaceId,
          targetWorkspace: targetWorkspaceId,
          model: workspaceInterface.getWorkspaceModelId(sourceWorkspaceId)!,
        },
      })
    );
  }
};

type HandleReferenceDeletionArgs = {
  id: ArdoqId;
  readOnly: boolean;
};

export const handleReferenceDeletion = async ({
  id,
  readOnly,
}: HandleReferenceDeletionArgs): Promise<void> => {
  const isSuppressConfirmDialog =
    window.sessionStorage.getItem(SESSION_KEY_DEPMATRIX_DELETE) === 'true';
  const refData = referenceInterface.get(id);
  const isValidReference = referenceInterface.isReference(id);
  const isReferenceLocked = lockOperations.isLocked(
    refData?.lock,
    getFeatureContext()
  );

  if (isReferenceLocked) {
    // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders alert() body once and does not re-render on locale change
    const i18nLocale = i18nLocale$.state;
    const unknown = t(
      i18nLocale,
      'tabview:dependencyMatrix.referenceCell.unknown',
      'Unknown'
    );
    await alert({
      title: t(
        i18nLocale,
        'tabview:dependencyMatrix.referenceCell.locked.title',
        'Reference is locked'
      ),
      subtitle: t(
        i18nLocale,
        'tabview:dependencyMatrix.referenceCell.locked.subtitle',
        'Unlock the reference to be able to delete it.'
      ),
      text: (
        <LockedReferenceDialogText
          i18nLocale={i18nLocale}
          referenceNames={[
            refData
              ? `${refData.source ? (componentInterface.getFullPathName(refData.source) ?? unknown) : unknown} -> ${refData.target ? (componentInterface.getFullPathName(refData.target) ?? unknown) : unknown}`
              : '',
          ]}
        />
      ),
      doqType: DoqType.LOCKED,
      modalSize: ModalSize.S,
    });

    return;
  }

  if (readOnly) return;

  if (!isSuppressConfirmDialog && isValidReference) {
    // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
    const i18nLocale = i18nLocale$.state;
    const { rememberAction, confirmed } = (await confirm({
      ...(await getDialogConfigByIds([], [id])),
      showRememberActionCheckbox: true,
      rememberActionCustomCheckboxText: t(
        i18nLocale,
        'tabview:dependencyMatrix.referenceCell.confirmDelete.dontAskAgain',
        "Don't ask again in this session."
      ),
      confirmButtonTitle: t(
        i18nLocale,
        'tabview:dependencyMatrix.referenceCell.confirmDelete.confirmButton',
        'Delete'
      ),
      isConfirmButtonDanger: true,
      modalSize: ModalSize.S,
    })) as ConfirmResult;

    if (rememberAction) {
      window.sessionStorage.setItem(SESSION_KEY_DEPMATRIX_DELETE, 'true');
    }

    if (!confirmed) return;
  }

  if (isValidReference) {
    const result = await dispatchActionAndWaitForResponse(
      deleteReferences({
        referenceIds: [id],
        isScenarioMode: isScenarioMode(),
      }),
      deleteReferencesSuccess,
      deleteReferencesFailed
    );
    if (isArdoqError(result.payload)) {
      // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders alert() body once and does not re-render on locale change
      const i18nLocale = i18nLocale$.state;
      alert({
        title: t(
          i18nLocale,
          'tabview:dependencyMatrix.referenceCell.deleteFailed.title',
          'Could not delete reference'
        ),
        text: t(
          i18nLocale,
          'tabview:dependencyMatrix.referenceCell.deleteFailed.text',
          'An error occurred while deleting the reference. The error has been reported, but please contact support if the error persists.'
        ),
      });
      logError(result.payload, 'dependencyMatrixDeleteReference error');
    }
  }
};
